import {
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Button,
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { useEffect, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  TimeOffPolicyDescriptions,
  TimeOffPolicyEnum,
} from "../../../enums/HR/TimeOffPolicyEnum";
import TimeOffRequestActionButtons from "../TimeOffRequestActionButtons/TimeOffRequestActionButtons";
import {
  MoneyRequestStatusType,
  statusColors,
} from "../../../data/FormColumns/Fleet/moneyRequestsTableColumns";
import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";
import { InfoOutlined, CheckCircle, Cancel } from "@mui/icons-material";
import { useMsal } from "@azure/msal-react";
import ProcessedByHrSwitch from "../ProcessedByHrSwitch/ProcessedByHrSwitch";
import { normalizeTimeOffDayPortion } from "../../../enums/HR/TimeOffDayPortionEnum";

const durationLabels = {
  fullDay: "Full Day",
  firstHalf: "First Half",
  secondHalf: "Second Half",
} as const;

const getDuration = (
  row: { dayPortion?: unknown; isHalfDay?: boolean } | null | undefined,
) =>
  durationLabels[normalizeTimeOffDayPortion(row?.dayPortion, row?.isHalfDay)];

const formatOptionalValue = (value: unknown) => {
  if (value === null || value === undefined) return "—";
  const formattedValue = String(value).trim();
  return formattedValue || "—";
};

const fetchTimeOffRequests = async (
  token: string | null | undefined,
  month?: number,
  year?: number,
  futureOnly?: boolean,
  pendingOnly?: boolean,
) => {
  const { data } = await axios.get(`${apiVariable}/getAllTimeOffs`, {
    headers: { Authorization: `Bearer ${token}` },
    params: {
      month,
      year,
      futureOnly,
      pendingOnly,
    },
  });
  return data;
};

const TimeOffRequestsTable = () => {
  const { token, role } = useAuth();
  const { accounts } = useMsal();
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState<number>(
    currentDate.getMonth() + 1,
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    currentDate.getFullYear(),
  );
  const [futureOnly, setFutureOnly] = useState(false);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [searchParams] = useSearchParams();
  const selectedRequestId = searchParams.get("requestId");

  useEffect(() => {
    if (selectedRequestId) {
      setShowAll(true);
      setFutureOnly(false);
      setPendingOnly(false);
    }
  }, [selectedRequestId]);
  const currentUserEmail = (
    accounts[0]?.username ||
    (accounts[0] as any)?.email ||
    ""
  ).toLowerCase();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      "timeOffRequests",
      selectedMonth,
      selectedYear,
      futureOnly,
      pendingOnly,
      showAll,
    ],
    queryFn: () => {
      const monthParam =
        futureOnly || pendingOnly || showAll ? undefined : selectedMonth;
      const yearParam =
        futureOnly || pendingOnly || showAll ? undefined : selectedYear;
      return fetchTimeOffRequests(
        token,
        monthParam,
        yearParam,
        futureOnly,
        showAll ? undefined : pendingOnly,
      );
    },
    enabled: !!token && role?.includes("HR"),
  });

  const columns: GridColDef[] = [
    {
      field: "employee",
      headerName: "Employee",
      flex: 1.3,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams) => {
        const firstName =
          params.row.firstName || params.row.employee?.firstName || "";
        const lastName =
          params.row.lastName || params.row.employee?.lastName || "";
        const fullName = `${firstName} ${lastName}`.trim();
        const managerObj = params.row.manager || params.row.employee?.manager;
        const managerEmail = (
          managerObj?.email ||
          managerObj?.Email ||
          ""
        ).toLowerCase();
        const reportsToMe =
          !!managerEmail &&
          !!currentUserEmail &&
          managerEmail === currentUserEmail;
        const showIndicator = !!currentUserEmail && !!managerEmail;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              height: "100%",
              px: 0.5,
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 22,
                display: "flex",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {showIndicator ? (
                <Tooltip
                  title={
                    reportsToMe
                      ? `Reports to you (${managerEmail})`
                      : `Manager: ${managerEmail || "N/A"}`
                  }
                  arrow
                >
                  {reportsToMe ? (
                    <CheckCircle fontSize="small" color="success" />
                  ) : (
                    <Cancel fontSize="small" color="error" />
                  )}
                </Tooltip>
              ) : null}
            </Box>
            <Typography
              variant="body2"
              title={fullName}
              sx={{
                flexGrow: 1,
                minWidth: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {fullName}
            </Typography>
          </Box>
        );
      },
      // Use MUI X v7 valueGetter signature: (value, row)
      valueGetter: (_value: any, row: any) => {
        if (!row) return "";
        return `${row.firstName || row.employee?.firstName || ""} ${
          row.lastName || row.employee?.lastName || ""
        }`.trim();
      },
    },
    {
      field: "employer",
      headerName: "Employer",
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueGetter: (value: unknown) => formatOptionalValue(value),
    },
    {
      field: "deliveryUnit",
      headerName: "Delivery Unit",
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueGetter: (value: unknown) => formatOptionalValue(value).toUpperCase(),
    },
    {
      field: "policy",
      headerName: "Policy",
      flex: role?.includes("HeadOfHR") ? 1.5 : 1,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {TimeOffPolicyEnum[params.value as string] || params.value}
          <Tooltip
            title={
              <Typography sx={{ whiteSpace: "pre-line" }}>
                {TimeOffPolicyDescriptions[params.value as string]}
              </Typography>
            }
            arrow
          >
            <InfoOutlined fontSize="small" sx={{ ml: 1 }} />
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "startDate",
      headerName: "Start Date",
      flex: 1,
      valueGetter: (params: any) => {
        if (!params) return "";
        return new Date(params).toLocaleDateString();
      },
    },
    {
      field: "endDate",
      headerName: "End Date",
      flex: 1,
      valueGetter: (params: any) => {
        if (!params) return "";
        return new Date(params).toLocaleDateString();
      },
    },
    {
      field: "duration",
      headerName: "Duration",
      type: "singleSelect",
      valueOptions: Object.values(durationLabels),
      flex: 1,
      valueGetter: (_value, row) => getDuration(row),
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const text: string = params.value || params.row.notes || "";
        const needsTruncate = text.length > 20;
        const display = needsTruncate ? `${text.slice(0, 20)}…` : text;
        const content = (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              px: 0.5,
            }}
          >
            <Typography
              variant="body2"
              noWrap
              sx={{
                maxWidth: "100%",
                cursor: needsTruncate ? "help" : "default",
                textOverflow: "ellipsis",
              }}
            >
              {display || "-"}
            </Typography>
          </Box>
        );
        return needsTruncate ? (
          <Tooltip
            title={
              <Box sx={{ whiteSpace: "pre-wrap", maxWidth: 400 }}>{text}</Box>
            }
            arrow
          >
            {content}
          </Tooltip>
        ) : (
          content
        );
      },
    },
    ...(role?.includes("HeadOfHR")
      ? [
          {
            field: "isHrProcessed",
            headerName: "Processed By HR",
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
              <ProcessedByHrSwitch
                id={params.row.id}
                isHrProcessed={params.row.isHrProcessed}
                isDisabled={params.row.status !== "approved"}
              />
            ),
          } as GridColDef,
        ]
      : []),

    {
      field: "hrNotes",
      headerName: "HR Notes",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const text: string = params.value || params.row.hrNotes || "";
        const needsTruncate = text.length > 20;
        const display = needsTruncate ? `${text.slice(0, 20)}…` : text;
        const content = (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 0.5,
              textAlign: "center",
            }}
          >
            <Typography
              variant="body2"
              noWrap
              sx={{
                maxWidth: "100%",
                cursor: needsTruncate ? "help" : "default",
                textOverflow: "ellipsis",
              }}
            >
              {display || "-"}
            </Typography>
          </Box>
        );
        return needsTruncate ? (
          <Tooltip
            title={
              <Box sx={{ whiteSpace: "pre-wrap", maxWidth: 400 }}>{text}</Box>
            }
            arrow
          >
            {content}
          </Tooltip>
        ) : (
          content
        );
      },
      valueGetter: (params: any) => params || "",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params: any) => (
        <Tooltip
          title={
            params.row.status === "approved"
              ? `Approved by ${params.row.reviewer}`
              : params.row.status === "declined"
                ? `Declined by ${params.row.reviewer}`
                : params.row.notes || ""
          }
        >
          <Chip
            label={statusColors[params.value as MoneyRequestStatusType].label}
            color={statusColors[params.value as MoneyRequestStatusType].color}
          />
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: role?.includes("HeadOfHR") ? 650 : 500,
      flex: role?.includes("HeadOfHR") ? 5 : 4,
      renderCell: (params: GridRenderCellParams) => {
        return <TimeOffRequestActionButtons row={params} />;
      },
    },
  ];

  const sanitizeForExport = (rows: any[]) => {
    if (!Array.isArray(rows)) return [];
    return rows.map((row) => {
      const firstName = row?.firstName || row?.employee?.firstName || "";
      const lastName = row?.lastName || row?.employee?.lastName || "";
      const fullName = `${firstName} ${lastName}`.trim();

      const policyLabel =
        TimeOffPolicyEnum[(row?.policy as string) || ""] || row?.policy || "";

      const statusKey = row?.status as MoneyRequestStatusType;
      const statusLabel = statusColors[statusKey]?.label || row?.status || "";

      const startDate = row?.startDate
        ? new Date(row.startDate).toLocaleDateString()
        : "";
      const endDate = row?.endDate
        ? new Date(row.endDate).toLocaleDateString()
        : "";

      return {
        employee: fullName,
        employer: formatOptionalValue(row?.employer),
        deliveryUnit: formatOptionalValue(row?.deliveryUnit),
        policy: policyLabel,
        startDate,
        endDate,
        duration: getDuration(row),
        requestedAt: row?.requestedAt,
        notes: row?.notes || "",
        isHrProcessed: row?.isHrProcessed ?? false,
        hrNotes: row?.hrNotes || "",
        status: statusLabel,
        reviewer: row?.reviewer || "",
        reviewedAt: row?.reviewedAt,
      };
    });
  };

  return (
    <Box className="employees-table-container" height={900}>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Grid container spacing={2} sx={{ width: "auto" }}>
          <Grid item>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                disabled={futureOnly || pendingOnly || showAll}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {new Date(2000, i).toLocaleString("default", {
                      month: "long",
                    })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                disabled={futureOnly || pendingOnly || showAll}
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - 2 + i;
                  return (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  checked={futureOnly}
                  onChange={(e) => {
                    setFutureOnly(e.target.checked);
                    if (e.target.checked) {
                      setShowAll(false);
                    } else {
                      setSelectedMonth(new Date().getMonth() + 1);
                      setSelectedYear(new Date().getFullYear());
                    }
                  }}
                  disabled={pendingOnly || showAll}
                />
              }
              label="Show future time off requests"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  checked={pendingOnly}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setPendingOnly(isChecked);
                    if (isChecked) {
                      setFutureOnly(false);
                      setShowAll(false);
                    } else {
                      setSelectedMonth(new Date().getMonth() + 1);
                      setSelectedYear(new Date().getFullYear());
                    }
                  }}
                  disabled={showAll}
                />
              }
              label="Show pending requests only"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showAll}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setShowAll(isChecked);
                    if (isChecked) {
                      setFutureOnly(false);
                      setPendingOnly(false);
                    } else {
                      setSelectedMonth(new Date().getMonth() + 1);
                      setSelectedYear(new Date().getFullYear());
                    }
                  }}
                />
              }
              label="Show all time off requests"
            />
          </Grid>
        </Grid>
        <Button
          component={RouterLink}
          to="/hr/time-off-calculations"
          variant="outlined"
        >
          Time Off Calculations
        </Button>
      </Box>
      {isLoading ? (
        <Box className="table-loader">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box className="table-error">
          <Typography color="error">
            Error:{" "}
            {error instanceof Error ? error.message : "An error occurred"}
          </Typography>
        </Box>
      ) : (
        <PersistedDataGrid
          tableName="timeOffRequests"
          mobileCompactView
          compactViewConfig={{
            primaryField: "employee",
            getCardTitle: (row) =>
              `${row.firstName || row.employee?.firstName || ""} ${row.lastName || row.employee?.lastName || ""}`.trim(),
            detailFields: [
              "employer",
              "deliveryUnit",
              "policy",
              "startDate",
              "endDate",
              "duration",
              "notes",
              "hrNotes",
              ...(role?.includes("HeadOfHR") ? ["isHrProcessed"] : []),
            ],
            statusField: "status",
            actionFields: ["actions"],
          }}
          rows={data}
          columns={columns.map((column) => ({
            ...column,
            minWidth:
              column.field === "employee"
                ? 220
                : column.field === "actions"
                  ? role?.includes("HeadOfHR")
                    ? 480
                    : 260
                  : 150,
          }))}
          sanitizeForExport={sanitizeForExport}
          sx={{
            height: 700,
            "& .selected-time-off-request": {
              bgcolor: "rgba(237, 108, 2, 0.16)",
              outline: "2px solid #ed6c02",
              outlineOffset: -2,
            },
          }}
          rowSelectionModel={selectedRequestId ? [selectedRequestId] : []}
          getRowClassName={(params) =>
            String(params.id) === selectedRequestId
              ? "selected-time-off-request"
              : ""
          }
        />
      )}
    </Box>
  );
};

export default TimeOffRequestsTable;
